import debounce from 'debounce-promise'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import {
  AutocompletePlaceParam,
  AutocompletionRequest,
} from 'tripPlanner/types/google'
import { getUniquePredictions } from 'tripPlanner/utils/places'

interface HookOptions {
  debounceDelay?: number
  characterThreshold?: number
  // The lat/long used when calcuating the distance in the preduction result
  origin?: google.maps.LatLngLiteral
  // Used to bias the results around the location at lat/long specified
  // if specified, must also include a radius
  biasLocation?: google.maps.LatLngLiteral
  // used to bias the results for a radius around the location, number is in meters
  biasRadius?: number
}

export type Prediction = google.maps.places.AutocompletePrediction

const usePlaceAutocomplete = (
  searchPhrase: string | undefined,
  placeTypes: AutocompletePlaceParam,
  {
    debounceDelay = 300,
    characterThreshold = 1,
    origin,
    biasLocation,
    biasRadius,
  }: HookOptions = {},
  autoCompletionRequest?: AutocompletionRequest,
) => {
  const { isLoaded: isServiceLoaded, autocompleteService } =
    useGoogleMapsContext()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [predictions, setPredictions] = useState<Prediction[]>([])

  const fetchPredictions = useCallback(
    async (request) => {
      if (autocompleteService)
        return autocompleteService.getPlacePredictions(request)
    },
    [autocompleteService],
  )

  const fetchMultiPredictions = useCallback(
    ({ value, placeTypes, autoCompletionRequest }) => {
      const { componentRestrictions, ...options } = autoCompletionRequest || {}

      Promise.all([
        fetchPredictions({
          input: value,
          types: placeTypes,
          componentRestrictions,
          ...options,
        }),
        fetchPredictions({
          input: value,
          types: placeTypes,
          ...options,
        }),
      ])
        .then((results) => {
          const predictions: Prediction[] = []
          results?.[0] && predictions.push(...results[0].predictions)
          results?.[1] && predictions.push(...results[1].predictions)

          setPredictions(getUniquePredictions(predictions))
          setIsLoading(false)
        })
        .catch(() => {
          setIsError(true)
          setPredictions([])
        })
    },
    [fetchPredictions],
  )

  const getDebouncedMultiGooglePredictions = useMemo(
    () =>
      debounce((value: string) => {
        if (autocompleteService) {
          fetchMultiPredictions({ value, placeTypes, autoCompletionRequest })
        }
      }, debounceDelay),
    [
      autoCompletionRequest,
      autocompleteService,
      debounceDelay,
      fetchMultiPredictions,
      placeTypes,
    ],
  )

  const getDebouncedGooglePredictions = useMemo(
    () =>
      debounce((value: string) => {
        if (autocompleteService) {
          setIsLoading(true)
          autocompleteService
            .getPlacePredictions(
              {
                input: value,
                types: placeTypes,
                origin,
                location: biasLocation
                  ? new google.maps.LatLng(biasLocation)
                  : undefined,
                radius: biasLocation ? biasRadius : undefined,
              },
              (results) => {
                setPredictions(results || [])
                setIsLoading(false)
              },
            )
            .catch(() => {
              setIsError(true)
              setPredictions([])
            })
        }
      }, debounceDelay),
    [
      autocompleteService,
      biasLocation,
      biasRadius,
      debounceDelay,
      origin,
      placeTypes,
    ],
  )

  useEffect(() => {
    if (
      searchPhrase &&
      searchPhrase.length > characterThreshold &&
      isServiceLoaded
    ) {
      setIsLoading(true)
      let getPredictionsFunc = getDebouncedGooglePredictions
      if (autoCompletionRequest) {
        const { componentRestrictions } = autoCompletionRequest
        getPredictionsFunc =
          componentRestrictions &&
          componentRestrictions.country?.length > 0 &&
          componentRestrictions.country?.length <= 5
            ? getDebouncedMultiGooglePredictions
            : getDebouncedGooglePredictions
      }
      getPredictionsFunc(searchPhrase)
    } else {
      setPredictions([])
    }
  }, [
    searchPhrase,
    getDebouncedGooglePredictions,
    characterThreshold,
    isServiceLoaded,
    autoCompletionRequest,
    getDebouncedMultiGooglePredictions,
  ])

  return {
    predictions,
    isLoading,
    isError,
    hasResults: predictions.length > 0,
  }
}

export default usePlaceAutocomplete
